import { action, computed, makeObservable } from 'mobx';
import moment from 'moment';
import { AxiosResponse } from 'axios';

import FORMATS from 'jsx/constants/FORMATS';
import AlertStore from 'jsx/stores/alertStore';
import url from 'jsx/utils/urlWrapper';
import { DASHBOARD_URLS } from 'jsx/dashboard/urls';
import { post, put, patch, axiosDelete } from 'jsx/utils/axiosWrapper';
import useStores from 'jsx/utils/hooks/useStores';
import { trackEvent } from 'jsx/utils/metrics';
import PatientModel, { UserResourceType } from 'jsx/models/patient';
import formattedAddressArray from 'jsx/dashboard/utils/formattedAddressArray';

import BaseUserStore from '../baseUserStore';

interface PaymentInfoData {
  paymentNonce: string;
  vendor: string;
  method: string;
  deviceData?: string;
  recaptchaToken?: string;
}

export interface ShipmentReturnResource {
  shipmentId: number;
  patientUserId: number;
  returnedAt: string | null;
  correctedAt: string | null;
  refundProcessedAt: string | null;
  cancellationProcessedAt: string | null;
}

// TODO-TS -- type out (data: Record<string, unknown>) typings
class UserStore extends BaseUserStore {
  constructor() {
    super();
    makeObservable(this);
  }

  @action
  updatePaymentInfo(data: PaymentInfoData): Promise<void> {
    return post(url('app.patient.payment'), data).then((res) => {
      this.hydrate(res.data.data.user);
    });
  }

  @action
  resubscribe = (
    automaticHold = true,
    note: string | null = null,
  ): Promise<PatientModel> =>
    put(url('app.patient.payment'), { automaticHold, note }).then((res) => {
      trackEvent('Subscription Reactivated');
      this.hydrate(res.data.data.user);
      return this.patient;
    });

  @action
  cancel(
    data: {
      cancelTypeId: number;
      note?: string;
    },
    shouldAlert?: boolean,
  ): Promise<void> {
    return axiosDelete(url('app.patient.payment'), { data }).then((res) => {
      this.hydrate(res.data.data.user);
      this.set('shouldRefresh', res.data.data.shouldRefresh);

      // TODO remove alert store notification from here.
      if (shouldAlert) {
        return AlertStore.add('Successfully cancelled', 'success');
      }
    });
  }

  @action
  pauseMembership(data: Record<string, unknown>): Promise<void> {
    return put(url('api.patient.shipments.pause'), data).then((res) => {
      this.hydrate(res.data.user);
    });
  }

  @action
  buyNow(): Promise<AxiosResponse<any>> {
    return post(url('api.patient.shipments.buyNow'), {}).then((res) => {
      this.hydrate(res.data.user);
      return res;
    });
  }

  @action
  registerCreditCard(data: Record<string, unknown>): Promise<void> {
    return post(url('app.patient.payment.registerCard'), data).then(
      (response) => {
        const { user } = response.data.data;
        this.hydrate(user);
      },
    );
  }

  @action
  rescheduleShipment(
    nextShipmentDate: moment.Moment | string,
  ): Promise<PatientModel> {
    let nextShipmentAt = nextShipmentDate;

    if (typeof nextShipmentDate === 'object') {
      // assume moment object
      nextShipmentAt = nextShipmentDate.format(FORMATS.date.yearMonthDay);
    }

    return put(url('api.patient.shipments.reschedule'), {
      nextShipmentAt,
    }).then((res) => this.hydrate(res.data.user));
  }

  /**
   * TODO-TS: We probably pass more into this method. Update types as
   * needed for future development.
   */
  @action
  updateContactInfo(data: {
    addressee_name?: string;
    address1: string;
    address2: string;
    city: string;
    state: string;
    phone: string;
    zipcode: string;
  }) {
    return patch<{
      user: UserResourceType;
      providerInfo: {
        providerChange: boolean;
        provider?: {
          firstname: string;
          lastname: string;
          profilePhotoUrl: string;
          signature: string;
        };
      };
      lastReturnedShipment?: ShipmentReturnResource;
    }>(url('app.patient.contactDetails'), data).then((response) => {
      const { user } = response.data;
      this.hydrate(user);

      return response;
    });
  }

  @action
  updateEmergencyContact(data: Record<string, unknown>): Promise<any> {
    return put(url('api.patient.emergencyContact'), data).then((response) => {
      const { user, providerInfo } = response.data;
      this.hydrate(user);
      return providerInfo;
    });
  }

  @action
  updateLoginDetails(
    data: Record<string, unknown>,
  ): Promise<AxiosResponse<any>> {
    return post(url('api.patient.loginDetails'), data).then((res) => {
      const { user } = res.data;
      this.hydrate(user);
      return res;
    });
  }

  @action
  updatePharmacy(data: Record<string, unknown>): Promise<void> {
    return put(url('api.patient.pharmacy'), data).then((response) => {
      const { user } = response.data;
      this.hydrate(user);
    });
  }

  @action
  sendEmailConfirmation(): Promise<AxiosResponse<UserResourceType>> {
    return post(url('api.patient.sendVerificationEmail'));
  }

  @action
  acceptCancellationDiscount(): Promise<void> {
    return post(DASHBOARD_URLS.api.acceptCancelDiscount()).then((response) => {
      const { user } = response.data;
      this.hydrate(user);
    });
  }

  @action
  activateUnbundling(): Promise<void> {
    return put(DASHBOARD_URLS.api.activateUnbundling()).then((response) => {
      this.hydrate(response.data.data);
    });
  }

  @computed
  get canEditShipmentDate(): boolean {
    const hasShipmentDate = !!this.patient.nextShipmentWillChargeAt;
    const hasShipmentProblem =
      this.patient.hasShipmentHolds || this.patient.hasPendingShipmentReturns;

    return hasShipmentDate && !hasShipmentProblem;
  }

  @computed
  get contactInfo() {
    const {
      firstname,
      lastname,
      addresseeName,
      address1,
      address2,
      city,
      state,
      zipcode,
      phone,
    } = this.patient;

    return {
      firstname,
      lastname,
      addresseeName,
      addressee_name: addresseeName,
      address1,
      address2,
      city,
      state,
      zipcode,
      phone,
    };
  }

  @computed
  // This will be deprecated when a user's shipping address always includes their addressee_name.
  get formattedCityStateAndZip(): string[] {
    const { address1, address2, city, state, zipcode } = this.patient;
    return formattedAddressArray({
      addresseeName: null,
      address1,
      address2,
      city,
      state,
      zipcode,
    });
  }

  @computed
  get formattedShippingAddress(): string[] {
    const { addresseeName, address1, address2, city, state, zipcode } =
      this.patient;
    return formattedAddressArray({
      addresseeName,
      address1,
      address2,
      city,
      state,
      zipcode,
    });
  }

  @computed
  // This varies from the above because address1 and address2 are combined into a single string.
  get formattedShippingAddressShort(): string[] {
    const { addresseeName, address1, address2, city, state, zipcode } =
      this.patient;
    const formatted = [];
    const streetAddress = address2 ? `${address1}, ${address2}` : address1;

    formatted.push(addresseeName);
    if (streetAddress) formatted.push(streetAddress);
    if (city && state && zipcode) {
      formatted.push(` ${city}, ${state} ${zipcode}`);
    }

    return formatted;
  }
}

export const useUserStore = (): { userStore: UserStore } => {
  const { userStore } = useStores();

  return { userStore };
};

export default UserStore;
